// deps
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// contexts
import { CartOverlaysContext, NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useShouldRenderLottie } from "@cart-client/hooks/useShouldRenderLottie";

// types
import { GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";

// helpers
import { isEmpty, getCloudinaryUrl } from "@swiggy-private/js-utils";
import { getRestaurantId } from "@cart-client/helpers/Cart";
import { modifySplashLottieText } from "@cart-client/helpers/savingsLottieHelpers";
import {
    isMagicItemsActive,
    hasMagicItemsLottiePlayedInSession,
} from "@cart-client/hooks/useCommonCartheaderLottie/helpers";

interface UseSplashLottieReturn {
    shouldRender: boolean;
    lottieData: any | null; // eslint-disable-line @typescript-eslint/no-explicit-any
    handleLottieComplete: VoidFunction;
    handleError: VoidFunction;
}

export const useSplashLottie = (onImpression?: (tag: string) => void): UseSplashLottieReturn => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { registerOverlays, enabledOverlayId, onCloseOverlay, onCloseOverlayImmediate } =
        useContext(CartOverlaysContext);
    const canRenderLottie = useShouldRenderLottie();

    const [isSkipped, setIsSkipped] = useState(false);
    const [lottieData, setLottieData] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
    const isLottieRegisteredRef = useRef(false);
    const hasCompletedRef = useRef(false);
    const hasImpressionFiredRef = useRef(false);
    const hasSeenOtherOverlay = useRef(false);
    const isFetchingRef = useRef(false);

    const splashLottieData = useMemo(
        () => cartResponse?.data?.cart_splash_lottie,
        [cartResponse?.data?.cart_splash_lottie],
    );

    const restaurantId = getRestaurantId(cartResponse);
    const sessionId = cartResponse?.sid;

    const frequencyCappingKey = useMemo(() => {
        if (isEmpty(restaurantId) || isEmpty(sessionId)) {
            return null;
        }
        return `${sessionId}-${restaurantId}`;
    }, [restaurantId, sessionId]);

    const lottieUrl = useMemo(() => {
        if (isEmpty(splashLottieData?.lottie_id)) {
            return null;
        }
        return getCloudinaryUrl(splashLottieData?.lottie_id) || null;
    }, [splashLottieData?.lottie_id]);

    const savingsAmount = useMemo(() => {
        const savingsBannerData = cartResponse?.data?.discount_message_v2;
        if (isEmpty(savingsBannerData)) {
            return 0;
        }

        const totalDiscount = savingsBannerData?.message_details?.total_discount;
        if (totalDiscount !== undefined && totalDiscount !== null) {
            return totalDiscount;
        }

        return 0;
    }, [cartResponse]);

    const tag = useMemo(() => splashLottieData?.cart_type ?? "", [splashLottieData?.cart_type]);

    // Fetch and modify Lottie JSON
    useEffect(() => {
        if (isEmpty(lottieUrl) || isSkipped) {
            return;
        }

        // Don't fetch until we have a valid savings amount
        if (!savingsAmount || savingsAmount <= 0) {
            return;
        }

        if (isFetchingRef.current) {
            return;
        }

        const fetchAndModifyLottie = async () => {
            isFetchingRef.current = true;
            try {
                const response = await fetch(lottieUrl);
                if (!response.ok) {
                    setIsSkipped(true);
                    isFetchingRef.current = false;
                    return;
                }

                let data = await response.json();
                data = modifySplashLottieText(data, savingsAmount);
                setLottieData(data);
                isFetchingRef.current = false;
            } catch (error) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.CART_LOTTIE_ERROR,
                    url: lottieUrl,
                    error: error instanceof Error ? error.name : JSON.stringify(error),
                    errorMessage: error instanceof Error ? error.message : JSON.stringify(error),
                    componentName: "SplashLottie",
                });
                setIsSkipped(true);
                isFetchingRef.current = false;
            }
        };

        fetchAndModifyLottie();
    }, [lottieUrl, savingsAmount, isSkipped]);

    // Check if we should skip due to magic items first before any other logic
    const shouldSkipForMagicItems = useMemo(() => {
        return isMagicItemsActive(cartResponse) && !hasMagicItemsLottiePlayedInSession(sessionId || "");
    }, [cartResponse, sessionId]);

    useEffect(() => {
        //  exit if magic items FTUE needs to play first
        if (shouldSkipForMagicItems) {
            setIsSkipped(true);
            // this is to overcome 99 splash lottie register before magic items ftue
            if (isLottieRegisteredRef.current) {
                onCloseOverlayImmediate();
            }
            return;
        }

        if (isEmpty(frequencyCappingKey) || isEmpty(lottieUrl) || isEmpty(splashLottieData?.lottie_key)) {
            setIsSkipped(true);
            return;
        }

        const storedKey = localStorage.getItem(splashLottieData?.lottie_key || "");

        if (!isEmpty(storedKey) && frequencyCappingKey === storedKey) {
            setIsSkipped(true);
        } else {
            if (!isLottieRegisteredRef.current) {
                registerOverlays({
                    overlayType: OverlayType.LOTTIE,
                    priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
                    id: LottieIds.CART_SPLASH_LOTTIE,
                });
                isLottieRegisteredRef.current = true;
            }
        }
    }, [
        shouldSkipForMagicItems,
        frequencyCappingKey,
        lottieUrl,
        splashLottieData?.lottie_key,
        registerOverlays,
        onCloseOverlayImmediate,
    ]);

    useEffect(() => {
        if (!isLottieRegisteredRef.current) {
            return;
        }

        if (enabledOverlayId && enabledOverlayId !== LottieIds.CART_SPLASH_LOTTIE) {
            if (!hasSeenOtherOverlay.current) {
                hasSeenOtherOverlay.current = true;
                setIsSkipped(true);
                onCloseOverlay();
            }
        }
    }, [enabledOverlayId, onCloseOverlay]);

    const shouldPlayLottie = useMemo(() => {
        if (isSkipped || isEmpty(lottieData)) {
            return false;
        }
        return enabledOverlayId === LottieIds.CART_SPLASH_LOTTIE && canRenderLottie;
    }, [enabledOverlayId, canRenderLottie, isSkipped, lottieData]);

    useEffect(() => {
        if (shouldPlayLottie && !hasImpressionFiredRef.current) {
            hasImpressionFiredRef.current = true;

            if (!isEmpty(splashLottieData?.lottie_key) && !isEmpty(frequencyCappingKey)) {
                localStorage.setItem(splashLottieData?.lottie_key ?? "", frequencyCappingKey);
            }

            if (onImpression) {
                onImpression(tag);
            }
        }
    }, [shouldPlayLottie, onImpression, splashLottieData?.lottie_key, frequencyCappingKey, tag]);

    const handleLottieComplete = useCallback(() => {
        if (!hasCompletedRef.current) {
            hasCompletedRef.current = true;
            onCloseOverlayImmediate();
        }
    }, [onCloseOverlayImmediate]);

    const handleError = useCallback(() => {
        handleLottieComplete();
    }, [handleLottieComplete]);

    useEffect(() => {
        return () => {
            if (isLottieRegisteredRef.current && !hasCompletedRef.current) {
                onCloseOverlay();
            }
        };
    }, [onCloseOverlay]);

    return {
        shouldRender: shouldPlayLottie && !isSkipped,
        lottieData,
        handleLottieComplete,
        handleError,
    };
};
